<template>
  <div class="xpage">
    <ValidationObserver v-slot="{ invalid }" ref="formValidationObserver">
      <!--Header-->
      <div class="page-header flex-shrink-0 shadow-sm">
        <div class="container-fluid px-md-4 px-3">
          <div class="row no-gutters d-flex justify-content-between">
            <h3 class="mb-0"><i class="fas fa-file-signature"></i>&nbsp;Add/Edit Insurance Policy <b-spinner
                class="ml-2" v-show="isBusy" small label="Small Spinner"></b-spinner></h3>
            <b-form inline>
              <div v-if="policy.id">
                <b-form-group>
                  <div class="d-flex mr-3">
                    <b-form-checkbox v-model="policy.isArchived" name="check-button" switch></b-form-checkbox>
                    <label class="control-label mr-2">Archive Policy</label>
                  </div>
                </b-form-group>
              </div>
              <div>
                <b-btn variant="secondary" @click="returnToPrevious">Cancel</b-btn>
                <b-btn variant="primary" @click="doSaveInsurancePolicy"
                  :disabled="invalid || !isDirty">Save</b-btn>
              </div>
            </b-form>
          </div>
        </div>
      </div>
                    
      <!--Content-->
      <div class="policies-container">
        <div class="container-fluid p-4">
          <div class="row mb-3">
            <div class="col-lg-6">
              <h3 class="mb-1">{{policy.carrierName}}</h3>
              <p class="mb-0">{{policy.number}}</p>
            </div>
            <div class="col-lg-6 d-flex justify-content-end align-items-end">
              <!-- Assignment Summary Section -->
              <div v-if="assignmentSummary" class="mt-3">
                <div class="badge py-1 px-2" :class="assignmentSummaryClass">
                  <i :class="assignmentSummaryIcon"></i> 
                  <strong>{{ assignmentSummary }}</strong>
                </div>
              </div>
            </div>
          </div>
            
          <!-- Computed Fields Section -->
          <div class="row">
            <div class="col-12 card-group">
              <div class="card">
                <div class="card-body">
                  <b-form-group class="mb-0 border-left-lg border-primary pl-3 h-100">
                    <label class="control-label text-muted">
                      <i class="fa fa-lock mr-1" title="Auto-calculated field"></i>
                      Previous Year Premium
                    </label>
                    <h2 class="mt-0">
                      {{ formatCurrency(policy.computed && policy.computed.previousYearPremium) }}
                    </h2>
                    <small class="form-text text-muted">Premium from the previous year's policy</small>
                  </b-form-group>
                </div>
              </div>
              <div class="card">
                <div class="card-body">
                  <b-form-group class="mb-0 border-left-lg border-muted pl-3 h-100">
                    <label class="control-label text-muted">
                      <i class="fa fa-lock mr-1" title="Auto-calculated field"></i>
                      Premium Change from Previous Year
                    </label>
                    <h2 
                      :class="[
                        'mt-0',
                        policy.computed && policy.computed.premiumChangeFromPreviousYear > 0 ? 'text-danger' : '',
                        policy.computed && policy.computed.premiumChangeFromPreviousYear < 0 ? 'text-success' : '',
                        (!policy.computed || policy.computed.premiumChangeFromPreviousYear === null) ? 'text-dark' : ''
                      ]"
                    >
                      {{ formatPercentChange(policy.computed && policy.computed.premiumChangeFromPreviousYear) }}
                    </h2>
                    <small 
                      class="form-text text-danger" 
                      v-if="policy.computed && policy.computed.premiumChangeFromPreviousYear > 0"
                    >
                      ▲ Premium increased from previous year
                    </small>
                    <small 
                      class="form-text text-success" 
                      v-if="policy.computed && policy.computed.premiumChangeFromPreviousYear < 0"
                    >
                      ▼ Premium decreased from previous year
                    </small>
                  </b-form-group>
                </div>
              </div>
              <div class="card">
                <div class="card-body">
                  <b-form-group class="mb-0 border-left-lg border-success pl-3 h-100">
                    <label class="control-label text-muted">
                      <i class="fa fa-lock mr-1" title="Auto-calculated field"></i>
                      Premium Per Unit
                      <i class="fa fa-info-circle text-muted ml-1" title="Premium divided by total residential units"></i>
                    </label>
                    <h2 class="mt-0">
                      {{ formatCurrency(policy.computed && policy.computed.premiumPerUnit) }}
                    </h2>
                    <small>&nbsp;</small>
                  </b-form-group>
                </div>
              </div>
              <div class="card">
                <div class="card-body">
                  <b-form-group class="mb-0 border-left-lg border-warning pl-3 h-100">
                    <label class="control-label text-muted">
                      <i class="fa fa-lock mr-1" title="Auto-calculated field"></i>
                      Total Units (Calculation Basis)
                      <i class="fa fa-info-circle text-muted ml-1" title="Unit count snapshot from assigned properties"></i>
                    </label>
                    <h2 class="mt-0">
                      {{ policy.computed && policy.computed.totalUnitsForPremiumCalculation != null ? policy.computed.totalUnitsForPremiumCalculation : 'N/A' }}
                    </h2>
                    <small>&nbsp;</small>
                  </b-form-group>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="px-lg-4 px-3 d-flex flex-column">
          <div class="row no-gutters">
            <div class="col-xl-8 col-lg-8 col-md-12 col-12 h-100 overflow-auto mb-5">

              <b-tabs content-class="mt-3 policy-tabs" card>
                <b-tab title="Policy" active>
                  <div class="form-container p-4 mb-3 radius-bottom">
                  <!--Policy Details-->
                  <div class="d-flex align-items-center mb-4">
                    <span class="fa-stack">
                      <i class="fas fa-circle fa-stack-2x text-secondary" aria-hidden="true"></i>
                      <i class="fas fa-list-ul fa-stack-1x fa-inverse" aria-hidden="true"></i>
                    </span>
                    <h5 class="text-dark m-0 pl-2">Policy Details</h5>
                  </div>
                  <div class="row">
                    <div class="col-xl-4 col-lg-6 col-12">
                      <b-form-group>
                        <ValidationProvider name="CarrierName" rules="required" v-slot="validation" immediate>
                          <label class="control-label">Carrier Name</label>
                          <b-form-input type="text" placeholder="Carrier Name" v-model="policy.carrierName"
                            :state="spr2_getValidationState(validation)"></b-form-input>
                        </ValidationProvider>
                      </b-form-group>
                    </div>
                    <div class="col-xl-4 col-lg-6 col-12">
                      <b-form-group>
                        <ValidationProvider name="PolicyNumber" rules="required" v-slot="validation" immediate>
                          <label class="control-label">Policy Number</label>
                          <span v-if="validationNumberError"><i class="fa fa-info-circle text-danger" :title="validationNumberError"></i></span>
                          <b-form-input type="text" placeholder="Policy Number" v-model="policy.number"
                            :state="spr2_getValidationState(validation) && validationNumberError == null"></b-form-input>
                        </ValidationProvider>
                      </b-form-group>
                    </div>
                    <div class="col-xl-4 col-lg-4 col-md-6 col-12">
                      <b-form-group>
                        <label class="control-label">Policy Type</label>
                        <ValidationProvider name="PolicyType" rules="required" v-slot="validation" immediate>
                          <b-form-select :options="policyTypes" v-model="policy.policyType"
                            :state="spr2_getValidationState(validation)">
                            <template #first>
                              <b-form-select-option :value="null" disabled :state="Boolean(false)">Select a
                                Type</b-form-select-option>
                            </template>
                          </b-form-select>
                        </ValidationProvider>
                      </b-form-group>
                    </div>
                    <div class="col-xl-4 col-lg-4 col-md-6 col-12">
                      <b-form-group>
                        <label class="control-label">Start Date</label>
                        <span v-if="validationStartDateError"><i class="fa fa-info-circle text-danger" :title="validationStartDateError"></i></span>
                        <ValidationProvider name="StartDate" rules="required" v-slot="validation" immediate>
                          <b-form-input class="form-control" type="date" :value="policy.startDate" name="startDate"
                            id="startDate" :state="spr2_getValidationState(validation) && validationStartDateError == null"
                            onclick="this.showPicker()" @change="policy.startDate = $event"></b-form-input>
                        </ValidationProvider>
                      </b-form-group>
                    </div>
                    <div class="col-xl-4 col-lg-4 col-md-6 col-12">
                      <b-form-group>
                        <label class="control-label">End Date</label>
                        <span v-if="validationEndDateError"><i class="fa fa-info-circle text-danger" :title="validationEndDateError"></i></span>
                        <ValidationProvider name="EndDate" rules="required" v-slot="validation" immediate>
                          <b-form-input class="form-control" type="date" :value="policy.endDate" name="endDate" id="endDate"
                            :state="spr2_getValidationState(validation) && validationEndDateError == null"
                            onclick="this.showPicker()" @change="policy.endDate = $event"></b-form-input>
                        </ValidationProvider>
                      </b-form-group>
                    </div>
                  </div>

                  <!--Adjuster-->
                  <div class="mb-4 mt-5 d-flex align-items-center">
                      <span class="fa-stack">
                        <i class="fas fa-circle fa-stack-2x text-secondary" aria-hidden="true"></i>
                        <i class="fas fa-list-ul fa-stack-1x fa-inverse" aria-hidden="true"></i>
                      </span>
                      <h5 class="text-dark m-0 pl-2">Adjuster</h5>
                    </div>
                  <div class="row">
                    <div class="col-lg-4 col-md-6 col-12">
                      <b-form-group>
                        <label class="control-label">Adjuster Name</label>
                        <b-input class="form-control" placeholder="Name" v-model="policy.adjusterName" />
                      </b-form-group>
                    </div>
                    <div class="col-lg-4 col-md-6 col-12">
                      <b-form-group>
                        <label class="control-label">Adjuster Phone Number</label>
                        <b-input class="form-control" placeholder="Phone Number" v-model="policy.adjusterPhoneNumber" />
                      </b-form-group>
                    </div>
                    <div class="col-lg-4 col-md-6 col-12">
                      <b-form-group>
                        <label class="control-label">Adjuster Email</label>
                        <b-input class="form-control" placeholder="Email" v-model="policy.adjusterEmail" />
                      </b-form-group>
                    </div>
                  </div>

                  <!--Coverage-->
                  <div class="mb-4 d-flex align-items-center mt-5">
                    <span class="fa-stack">
                      <i class="fas fa-circle fa-stack-2x text-secondary" aria-hidden="true"></i>
                      <i class="fas fa-list-ul fa-stack-1x fa-inverse" aria-hidden="true"></i>
                    </span>
                    <h5 class="text-dark m-0 pl-2">Coverage</h5>
                  </div>
                  <div class="row">
                    <div class="col-xl-3 col-lg-4 col-md-6 col-12">
                      <b-form-group>
                        <label class="control-label">Premium</label>
                        <currency-input v-model="policy.premium" />
                      </b-form-group>
                    </div>
                    <div class="col-xl-3 col-lg-4 col-md-6 col-12">
                      <b-form-group>
                        <label class="control-label">Premium Annualized</label>
                        <currency-input v-model="policy.premiumAnnualized" />
                      </b-form-group>
                    </div>
                    <div class="col-xl-3 col-lg-4 col-md-6 col-12">
                      <b-form-group>
                        <label class="control-label">Coverage Amount</label>
                        <currency-input v-model="policy.coverageAmount" />
                      </b-form-group>
                    </div>
                    <div class="col-xl-3 col-lg-4 col-md-6 col-12">
                      <b-form-group>
                        <label class="control-label">State Tax & Fees
                          <i class="fa fa-info-circle text-muted ml-1" title="State-mandated taxes and regulatory fees"></i>
                        </label>
                        <currency-input v-model="policy.stateTaxFees" />
                      </b-form-group>
                    </div>
                    <div class="col-xl-3 col-lg-4 col-md-6 col-12">
                      <b-form-group>
                        <label class="control-label">Other Tax & Fees
                          <i class="fa fa-info-circle text-muted ml-1" title="Additional taxes, fees, or surcharges"></i>
                        </label>
                        <currency-input v-model="policy.otherTaxFees" />
                      </b-form-group>
                    </div>
                  </div>
                  </div>

                  <!-- Deductible Terms -->
                  <div class="form-container p-4 mb-3">
                  <div class="mb-4 d-flex align-items-center">
                    <span class="fa-stack">
                      <i class="fas fa-circle fa-stack-2x text-secondary" aria-hidden="true"></i>
                      <i class="fas fa-list-ul fa-stack-1x fa-inverse" aria-hidden="true"></i>
                    </span>
                    <h5 class="text-dark m-0 pl-2">
                      Deductible Terms
                      <i class="fa fa-info-circle text-muted ml-1" title="Define one or more deductible terms. Each term covers a specific peril and basis."></i>
                    </h5>
                  </div>
                  <div v-if="policy.deductibleTerms.length === 0" class="text-muted p-3 border rounded bg-light mb-3">
                    <i class="fas fa-info-circle mr-2"></i>No deductible terms added. Click "Add Deductible Term" to add one.
                  </div>
                  <div v-for="(term, termIndex) in policy.deductibleTerms" :key="termIndex" class="border rounded p-3 mb-3 bg-white">
                    <div class="d-flex justify-content-between align-items-center mb-2">
                      <strong class="text-secondary">Term #{{ termIndex + 1 }}</strong>
                      <b-btn variant="link" size="sm" class="text-danger p-0" @click="removeDeductibleTerm(termIndex)">
                        <i class="fas fa-trash"></i> Remove
                      </b-btn>
                    </div>
                    <div class="row">
                      <div class="col-xl-3 col-lg-4 col-md-6 col-12">
                        <b-form-group>
                          <label class="control-label">Peril</label>
                          <b-form-select v-model="term.peril" :options="deductiblePerilOptions">
                            <template #first>
                              <b-form-select-option :value="null">Select Peril</b-form-select-option>
                            </template>
                          </b-form-select>
                        </b-form-group>
                      </div>
                      <div class="col-xl-3 col-lg-4 col-md-6 col-12">
                        <b-form-group>
                          <label class="control-label">Basis</label>
                          <b-form-select v-model="term.basis" :options="deductibleBasisOptions">
                            <template #first>
                              <b-form-select-option :value="null">Select Basis</b-form-select-option>
                            </template>
                          </b-form-select>
                        </b-form-group>
                      </div>
                      <div class="col-xl-3 col-lg-4 col-md-6 col-12">
                        <b-form-group>
                          <label class="control-label">Amount</label>
                          <currency-input v-if="!term.isPercentage" v-model="term.amount" />
                          <b-form-input v-else type="number" v-model="term.amount" min="0" max="100" step="0.01" placeholder="%" />
                        </b-form-group>
                      </div>
                      <div class="col-xl-3 col-lg-4 col-md-6 col-12 d-flex align-items-center">
                        <b-form-group class="mb-0">
                          <label class="control-label d-block">Is Percentage</label>
                          <b-form-checkbox v-model="term.isPercentage" switch>
                            {{ term.isPercentage ? 'Percentage (%)' : 'Dollar ($)' }}
                          </b-form-checkbox>
                        </b-form-group>
                      </div>
                      <div class="col-xl-3 col-lg-4 col-md-6 col-12">
                        <b-form-group>
                          <label class="control-label">Minimum Amount <small class="text-muted">(optional)</small></label>
                          <currency-input v-model="term.minimumAmount" />
                        </b-form-group>
                      </div>
                      <div class="col-xl-3 col-lg-4 col-md-6 col-12">
                        <b-form-group>
                          <label class="control-label">Maximum Amount <small class="text-muted">(optional)</small></label>
                          <currency-input v-model="term.maximumAmount" />
                        </b-form-group>
                      </div>
                      <div class="col-xl-3 col-lg-4 col-md-6 col-12">
                        <b-form-group>
                          <label class="control-label">Aggregate Limit <small class="text-muted">(optional)</small></label>
                          <currency-input v-model="term.aggregateLimit" />
                        </b-form-group>
                      </div>
                      <div class="col-xl-3 col-lg-4 col-md-6 col-12">
                        <b-form-group>
                          <label class="control-label">Trailing Amount <small class="text-muted">(optional)</small></label>
                          <currency-input v-model="term.trailingAmount" />
                        </b-form-group>
                      </div>
                      <div class="col-xl-3 col-lg-4 col-md-6 col-12 d-flex align-items-center">
                        <b-form-group class="mb-0">
                          <label class="control-label d-block">Is Per Building</label>
                          <b-form-checkbox v-model="term.isPerBuilding" switch>
                            {{ term.isPerBuilding ? 'Yes' : 'No' }}
                          </b-form-checkbox>
                        </b-form-group>
                      </div>
                      <div class="col-xl-3 col-lg-4 col-md-6 col-12 d-flex align-items-center">
                        <b-form-group class="mb-0">
                          <label class="control-label d-block">Is Included</label>
                          <b-form-checkbox v-model="term.isIncluded" switch>
                            {{ term.isIncluded ? 'Yes' : 'No' }}
                          </b-form-checkbox>
                        </b-form-group>
                      </div>
                    </div>
                  </div>
                  <b-btn variant="outline-success" size="sm" @click="addDeductibleTerm">
                    <i class="fas fa-plus mr-1"></i>Add Deductible Term
                  </b-btn>
                  </div>

                  <!-- Coverage Lines -->
                  <div class="form-container p-4">
                  <div class="mb-4 d-flex align-items-center">
                    <span class="fa-stack">
                      <i class="fas fa-circle fa-stack-2x text-secondary" aria-hidden="true"></i>
                      <i class="fas fa-list-ul fa-stack-1x fa-inverse" aria-hidden="true"></i>
                    </span>
                    <h5 class="text-dark m-0 pl-2">
                      Coverage Lines
                      <i class="fa fa-info-circle text-muted ml-1" title="Define one or more lines of coverage with their types, limits, and notes."></i>
                    </h5>
                  </div>
                  <div v-if="policy.coverageLines.length === 0" class="text-muted p-3 border rounded bg-light mb-3">
                    <i class="fas fa-info-circle mr-2"></i>No coverage lines added. Click "Add Coverage Line" to add one.
                  </div>
                  <div v-for="(line, lineIndex) in policy.coverageLines" :key="lineIndex" class="border rounded p-3 mb-3 bg-white">
                    <div class="d-flex justify-content-between align-items-center mb-2">
                      <strong class="text-secondary">Line #{{ lineIndex + 1 }}</strong>
                      <b-btn variant="link" size="sm" class="text-danger p-0" @click="removeCoverageLine(lineIndex)">
                        <i class="fas fa-trash"></i> Remove
                      </b-btn>
                    </div>
                    <div class="row">
                      <div class="col-xl-4 col-lg-6 col-12">
                        <b-form-group>
                          <label class="control-label">Line of Coverage</label>
                          <b-form-select
                            v-if="lineOfCoverageOptions.length > 0"
                            v-model="line.lineOfCoverageId"
                            :options="lineOfCoverageOptions"
                            @change="onCoverageLineLocChanged(line, $event)">
                            <template #first>
                              <b-form-select-option :value="null">Select a Line of Coverage</b-form-select-option>
                            </template>
                          </b-form-select>
                          <b-form-input v-else type="text" v-model="line.lineOfCoverageName" placeholder="Line of Coverage Name" />
                        </b-form-group>
                      </div>
                      <div class="col-xl-4 col-lg-6 col-12">
                        <b-form-group>
                          <label class="control-label">Coverage Types</label>
                          <div class="d-flex w-100" :class="{ 'border border-success rounded': line.coverageTypes && line.coverageTypes.length }">
                            <multiselect
                              v-model="line.coverageTypes"
                              :options="getCoverageTypesForLine(line.lineOfCoverageId)"
                              track-by="value"
                              label="text"
                              placeholder="Select Coverage Type(s)"
                              selectLabel=""
                              :multiple="true"
                              :searchable="true"
                              :close-on-select="false"
                              :taggable="false"
                              :disabled="!line.lineOfCoverageId">
                              <template slot="selection" slot-scope="{ values }">
                                <span class="multiselect__single" v-if="values.length">{{ values.length }} type{{ values.length > 1 ? 's' : '' }} selected</span>
                              </template>
                            </multiselect>
                          </div>
                        </b-form-group>
                      </div>
                      <div class="col-xl-4 col-lg-6 col-12">
                        <b-form-group>
                          <label class="control-label">Limit <small class="text-muted">(optional)</small></label>
                          <currency-input v-model="line.limit" />
                        </b-form-group>
                      </div>
                      <div class="col-12">
                        <b-form-group>
                          <label class="control-label">Notes <small class="text-muted">(optional)</small></label>
                          <b-form-textarea v-model="line.notes" placeholder="Coverage line notes" rows="2" max-rows="4" />
                        </b-form-group>
                      </div>
                    </div>
                  </div>
                  <b-btn variant="outline-success" size="sm" @click="addCoverageLine">
                    <i class="fas fa-plus mr-1"></i>Add Coverage Line
                  </b-btn>
                  </div>
                </b-tab>
                <b-tab title="Notes">
                  <b-form-group class="form-container p-4 radius-bottom">
                    <label class="control-label">Notes</label>
                    <b-form-textarea 
                      v-model="policy.notes" 
                      placeholder="Additional policy notes or comments"
                      rows="3"
                      max-rows="6"
                    ></b-form-textarea>
                  </b-form-group>
                </b-tab>
              </b-tabs>
            </div>

            <!--Properties-->
            <div class="col-xl-4 col-lg-4 col-md-12 col-12 px-xl-5 p-lg-4 p-3 h-100 overflow-auto">
              <div class="mb-2 d-flex align-items-center">
                <h5 class="text-dark m-0 pl-2">Properties</h5>
              </div>

              <div class="card mb-3">
                <div class="card-body">
                  <b-form-group>
                    <label class="control-label">
                      Associated Properties
                      <i class="fas fa-question-circle text-muted ml-1" 
                        v-b-tooltip.hover 
                        title="Select individual properties to cover under this policy. Properties with existing claims cannot be removed."></i>
                    </label>
                    <div class="d-flex w-100"
                      :class="{ 'border border-success rounded': selectedProperties.length, 'disabled-multiselect': policy.applyToAllProperties }">
                      <multiselect v-model="selectedProperties" :options="selectableProperties" track-by="value" label="text"
                        placeholder="Search properties..." selectLabel="" :multiple="true" :searchable="true" :close-on-select="false"
                        :taggable="false" :disabled="policy.applyToAllProperties" @select="itemAdded" @remove="itemRemoved"
                        aria-label="Select individual properties">
                        <template slot="selection" slot-scope="{ values }">
                          <span class="multiselect__single" v-if="values.length">{{ values.length }} properties
                            selected</span>
                        </template>
                      </multiselect>
                  </div>
                  </b-form-group>
                  <div>
                    <b-table v-show="selectedProperties.length" bordered class="rg-table" :items="selectedProperties"
                      :fields="fields">
                      <template v-slot:cell(id)="data">
                        <div class="text-right">
                          <!--clickable version with no claims-->
                          <div
                            v-if="data.item.claimCount === 0"
                            class="btn btn-link"><i 
                            class="fas fa-trash text-danger"
                            @click="removeProperty(data.item.value)"></i></div>                    
                          <!--Non-clickable version with claims-->
                          <div
                            v-if="data.item.claimCount > 0"
                            class="btn btn-link"><i 
                            class="fas fa-trash text-secondary" title="Properties with claims cannot be removed from the policy"></i></div>
                        </div>
                      </template>
                    </b-table>
                  </div>
                </div>
                <div class="card-footer py-3">
                  <div class="card mb-0 rounded" :class="{ 'border-success': policy.applyToAllProperties }">
                    <div class="card-body" :class="{ 'bg-success-light': policy.applyToAllProperties }">
                      <b-form-checkbox 
                        v-model="policy.applyToAllProperties" 
                        @change="onApplyToAllChanged"
                        size="md"
                        switch
                        aria-label="Apply policy to all properties">
                        <strong>Apply to All Properties</strong>
                        <i class="fas fa-globe ml-1" v-if="policy.applyToAllProperties"></i>
                      </b-form-checkbox>
                      <p class="text-muted small mb-0 mt-2" v-if="!policy.applyToAllProperties">
                        Check this box to apply this policy to all properties in your company (current and future).
                      </p>
                      <div v-if="policy.applyToAllProperties" class="alert alert-success mt-2 mb-0">
                        <i class="fas fa-check-circle"></i> 
                        <strong>This policy applies to ALL properties.</strong>
                        <br>
                        <small>Individual property and group selections below will be ignored while this is enabled.</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Property Groups Section -->
              <div class="card">
                <div class="card-body">
                  <b-form-group>
                    <label class="control-label">
                      Property Groups
                      <i class="fas fa-question-circle text-muted ml-1" 
                        v-b-tooltip.hover 
                        title="Property groups allow you to assign this policy to multiple properties at once. Groups with existing claims cannot be removed."></i>
                    </label>
                    <div class="d-flex w-100"
                      :class="{ 'border border-info rounded': selectedPropertyGroups.length, 'disabled-multiselect': policy.applyToAllProperties }">
                      <multiselect v-model="selectedPropertyGroups" :options="selectablePropertyGroups" track-by="value" label="text"
                        placeholder="Search property groups..." selectLabel="" :multiple="true" :searchable="true" :close-on-select="false"
                        :taggable="false" :disabled="policy.applyToAllProperties" @select="propertyGroupAdded" @remove="propertyGroupRemoved"
                        aria-label="Select property groups">
                        <template slot="selection" slot-scope="{ values }">
                          <span class="multiselect__single" v-if="values.length">{{ values.length }} group{{ values.length !== 1 ? 's' : '' }}
                            selected</span>
                        </template>
                        <template slot="option" slot-scope="props">
                          <div v-if="props.option.value">
                            {{ props.option.text }}
                            <b-badge variant="info" class="ml-2">{{ props.option.propertyCount }} properties</b-badge>
                          </div>
                          <div v-else>{{ props.option.text }}</div>
                        </template>
                      </multiselect>
                    </div>
                    <!-- <small class="text-muted" v-if="!policy.applyToAllProperties">
                      Property groups allow you to assign policies to multiple properties at once.
                      <router-link :to="{ name: 'PropertyGroups' }" target="_blank" class="ml-1">
                        <i class="fas fa-external-link-alt"></i> Manage Groups
                      </router-link>
                    </small> -->
                  </b-form-group>
                  <div>
                    <b-table v-show="selectedPropertyGroups.length" bordered class="mb-5 rg-table" :items="selectedPropertyGroups"
                      :fields="[
                        {key: 'text', label: 'Group Name', class: 'w-100'},
                        {key: 'propertyCount', label: 'Properties', class: 'text-center'},
                        {key: 'claimCount', label: 'Claims', class: 'text-center'},
                        {key: 'id', label: 'Remove', class: 'text-right'}
                      ]">
                      <template v-slot:cell(text)="data">
                        <i class="fas fa-layer-group text-info"></i> {{ data.value }}
                      </template>
                      <template v-slot:cell(propertyCount)="data">
                        <b-badge variant="info">{{ data.value }}</b-badge>
                      </template>
                      <template v-slot:cell(claimCount)="data">
                        <b-badge :variant="data.value > 0 ? 'danger' : 'secondary'">{{ data.value || 0 }}</b-badge>
                      </template>
                      <template v-slot:cell(id)="data">
                        <div class="text-right">
                          <!--clickable version with no claims-->
                          <div
                            v-if="data.item.claimCount === 0"
                            class="btn btn-link"><i 
                            class="fas fa-trash text-danger"
                            @click="removePropertyGroup(data.item.value)"></i></div>                    
                          <!--Non-clickable version with claims-->
                          <div
                            v-if="data.item.claimCount > 0"
                            class="btn btn-link"><i 
                            class="fas fa-trash text-secondary" title="Property groups with claims cannot be removed from the policy"></i></div>
                        </div>
                      </template>
                    </b-table>
                  </div>
                </div>
              </div>

              <!-- Validation Warnings Section -->
              <div v-if="hasValidationWarnings" class="mt-3">
                <div class="alert alert-warning">
                  <h6><i class="fas fa-exclamation-triangle"></i> Validation Warnings</h6>
                  <ul class="mb-0">
                    <li v-for="(warning, index) in validationWarnings" :key="index">
                      <strong>{{ warning.field }}:</strong> {{ warning.message }}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-md-6 col-sm-12">
              <div class="row">
              </div>
            </div>
            <!-- <div class="col-md-6 col-sm-12">
            <b-form-group>
              <label class="control-label">Properties</label>
            <ValidationProvider name="Property" rules="required" v-slot="validation" immediate>
              <b-form-select class="form-control" :options="selectableProperties" v-model="policy.propertyIds" multiple :state="spr2_getValidationState(validation)"></b-form-select>
            </ValidationProvider>
            </b-form-group>
          </div> -->

          </div>
        </div>
      </div>
    </ValidationObserver>
  </div>
</template>

<script>
import { spr2Validation } from '@/mixins/Spr2SharedFunctions'
import CurrencyInput from '@/components/CurrencyInput';
import moment from 'moment'
import { mapActions } from 'vuex'


export default {
  mixins: [spr2Validation],
  components: {
    CurrencyInput
  },
  name: 'AddEditInsurancePolicy',
  data () {
    return {
        isBusy: false,
        policy: {
          id: null,
          carrierName: null,
          policyAmount: null,
          policyType: null,
          startDate: null,
          endDate: null,
          premium: null,
          premiumAnnualized: null,
          coverageAmount: null,
          // Kept for backward compat — API may still return these; new UI uses deductibleTerms
          standardDeductible: null,
          specialDeductibles: [],
          stateTaxFees: null,
          otherTaxFees: null,
          notes: null,
          adjusterName: null,
          adjusterPhoneNumber: null,
          adjusterEmail: null,
          isArchived: false,
          propertyIds: [],
          propertyGroupIds: [],        // NEW: For property group assignment
          applyToAllProperties: false, // NEW: For apply to all functionality
          lineOfCoverage: null,
          // New structured fields replacing standardDeductible/specialDeductibles and lineOfCoverage/coverageTypes
          deductibleTerms: [],
          coverageLines: [],
          computed: {
            previousYearPremium: null,
            previousYearPolicyId: null,
            premiumPerUnit: null,
            totalUnitsForPremiumCalculation: null,
            premiumChangeFromPreviousYear: null
          }
        },
        fields: [{key: 'text', label: 'Property', class: 'w-100'},  { key: 'id', label: 'Remove', class: 'text-right' } ],
        originalPolicy: null,
        selectedProperties: [],
        selectedPropertyGroups: [],  // NEW: Store selected property groups
        selectedLineOfCoverageId: null,
        selectedCoverageTypes: [],
        // Deductible term peril options (used in DeductibleTerms editor)
        deductiblePerilOptions: [
          { value: 'AllPeril', text: 'All Peril' },
          { value: 'Named', text: 'Named' },
          { value: 'Wind', text: 'Wind' },
          { value: 'Flood', text: 'Flood' },
          { value: 'Earthquake', text: 'Earthquake' },
          { value: 'Other', text: 'Other' }
        ],
        // Deductible term basis options (used in DeductibleTerms editor)
        deductibleBasisOptions: [
          { value: 'ACV', text: 'ACV' },
          { value: 'RCV', text: 'RCV' },
          { value: 'StateValue', text: 'State Value' },
          { value: 'Other', text: 'Other' }
        ],
        policyTypes: [{ value: 0, text: 'General Liability' }, { value: 1, text: 'Umbrella' }, { value: 2, text: 'Property And Casualty' }],
        datePickerOpen: false,
        startDateDebounceTimer: null,
        propertyId: null,
        validationErrors: [],
        // AI extraction pre-populate
        aiSuggestion: null,
        aiSuggestionApplied: false
    }
  },
  async created() {
    const policyId = this.$route.params.id
    const propertyId = this.$route.query.propertyId

    if (policyId) {
      this.policy.id = policyId
    }

    if(propertyId) {
      this.propertyId = propertyId
    }

    // Capture and immediately clear the AI extraction result from the store so that
    // navigating back to this page later doesn't accidentally re-apply the suggestion.
    const importExportState = this.$store.state.importExport
    if (importExportState && importExportState.aiExtractionResult && importExportState.aiExtractionResult.suggestedPolicy) {
      this.aiSuggestion = importExportState.aiExtractionResult
      this.$store.commit('importExport/CLEAR_AI_EXTRACTION')
    }
  },
  async mounted() {
      await this.doGetInsurancePolicy()
      // Apply AI-extracted field values after the policy form is loaded.
      if (this.aiSuggestion && this.aiSuggestion.suggestedPolicy) {
        this.applyAiSuggestion(this.aiSuggestion.suggestedPolicy)
      }
  },
  methods: {
    ...mapActions('company', ['saveInsurancePolicy','getInsurancePolicyForEdit']),
    async doSaveInsurancePolicy () {
      // Backward compat: keep lineOfCoverage/coverageTypes in payload from selectedLineOfCoverageId
      // so that old API consumers still receive familiar fields alongside the new coverageLines array.
      if (this.selectedLineOfCoverageId) {
        const selectedLine = this.policy.selectableLinesOfCoverage
          ? this.policy.selectableLinesOfCoverage.find(line => line.id === this.selectedLineOfCoverageId)
          : null
        if (selectedLine) {
          this.policy.lineOfCoverage = {
            id: selectedLine.id,
            name: selectedLine.name,
            isActive: selectedLine.isActive
          }
          if (this.selectedCoverageTypes && this.selectedCoverageTypes.length > 0) {
            this.policy.coverageTypes = this.selectedCoverageTypes.map(sct => {
              const fullType = selectedLine.coverageTypes.find(ct => ct.id === sct.value)
              return { id: fullType.id, name: fullType.name }
            })
          } else {
            this.policy.coverageTypes = []
          }
        }
      } else {
        this.policy.lineOfCoverage = null
        this.policy.coverageTypes = []
      }

      // Ensure propertyGroupIds and applyToAllProperties are present for backward compat
      if (!this.policy.propertyGroupIds) {
        this.policy.propertyGroupIds = []
      }
      if (this.policy.applyToAllProperties === undefined || this.policy.applyToAllProperties === null) {
        this.policy.applyToAllProperties = false
      }

      // deductibleTerms and coverageLines are already on this.policy and will be sent as-is.
      // The API handles migration from old standardDeductible/specialDeductibles shape on save.

      let result = await this.saveInsurancePolicy(this.policy)
      if(result.success){
        this.$toasted.success(result.message, { duration: 4000 })
        if(this.propertyId) {
          this.$router.push({ name: 'PropertyInsurancePolicyDetails', params: { id: this.propertyId } }) // using route name
        } else {
          this.$router.push({ name: 'InsurancePolicies' }) // using route name
        }
      } else {
        this.$toasted.error(result.message, { duration: 4000 })
        this.validationErrors = result.validationErrors
      }
    },
    async returnToPrevious () {
      if(this.propertyId) {
        this.$router.push({ name: 'PropertyInsurancePolicyDetails', params: { id: this.propertyId } }) // using route name
      } else {
        this.$router.push({ name: 'InsurancePolicies' }) // using route name
      }
    },
    applyAiSuggestion (suggested) {
      const parseNum = s => { if (!s) return null; const n = parseFloat(String(s).replace(/[^0-9.-]/g, '')); return isNaN(n) ? null : n }
      const parseDate = s => {
        if (!s) return null
        const parts = s.split('/')
        if (parts.length === 3) return `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`
        return null
      }
      const parsePolicyType = s => {
        if (!s) return null
        const u = s.toUpperCase()
        if (u.includes('GENERAL')) return 0
        if (u.includes('UMBRELLA')) return 1
        if (u.includes('PROPERTY')) return 2
        return null
      }
      if (suggested.policyNumber) this.policy.number = suggested.policyNumber
      if (suggested.carrierName) this.policy.carrierName = suggested.carrierName
      if (suggested.policyType) { const pt = parsePolicyType(suggested.policyType); if (pt !== null) this.policy.policyType = pt }
      if (suggested.startDate) this.policy.startDate = parseDate(suggested.startDate) || suggested.startDate
      if (suggested.endDate) this.policy.endDate = parseDate(suggested.endDate) || suggested.endDate
      if (suggested.premium) { const v = parseNum(suggested.premium); if (v !== null) this.policy.premium = v }
      if (suggested.premiumAnnualized) { const v = parseNum(suggested.premiumAnnualized); if (v !== null) this.policy.premiumAnnualized = v }
      if (suggested.coverageAmount) { const v = parseNum(suggested.coverageAmount); if (v !== null) this.policy.coverageAmount = v }
      if (suggested.standardDeductible) { const v = parseNum(suggested.standardDeductible); if (v !== null) this.policy.standardDeductible = v }
      if (suggested.stateTaxFees) { const v = parseNum(suggested.stateTaxFees); if (v !== null) this.policy.stateTaxFees = v }
      if (suggested.otherTaxFees) { const v = parseNum(suggested.otherTaxFees); if (v !== null) this.policy.otherTaxFees = v }
      if (suggested.adjusterName) this.policy.adjusterName = suggested.adjusterName
      if (suggested.adjusterEmail) this.policy.adjusterEmail = suggested.adjusterEmail
      if (suggested.adjusterPhone) this.policy.adjusterPhoneNumber = suggested.adjusterPhone
      if (suggested.notes) this.policy.notes = suggested.notes
      if (suggested.applyToAllProperties) this.policy.applyToAllProperties = suggested.applyToAllProperties === 'Y'
      this.aiSuggestionApplied = true
    },
    isAmountValid (amount) {
      return (!isNaN(amount) && amount > 0)
    },
    async doGetInsurancePolicy() {
      this.isBusy = true
      let result = await this.getInsurancePolicyForEdit(this.policy.id)
      Object.assign(this.policy, result.data.policy)
      this.policy.propertyIds.forEach((pid) => {
        let p = this.selectableProperties.find(sp => sp.value === pid)
        if(p != null){
          this.selectedProperties.push(p)
        }
      })
      
      this.selectableProperties.sort((a, b) => a.text.localeCompare(b.text));

      // NEW: Handle property groups from API response
      if (this.policy.propertyGroupIds && this.policy.propertyGroupIds.length > 0) {
        this.policy.propertyGroupIds.forEach((gid) => {
          let g = this.selectablePropertyGroups.find(sg => sg.value === gid)
          if (g != null) {
            this.selectedPropertyGroups.push(g)
          }
        })
        this.selectedPropertyGroups.sort((a, b) => a.text.localeCompare(b.text));
      }

      // NEW: Ensure backward compatibility for applyToAllProperties
      if (this.policy.applyToAllProperties === undefined || this.policy.applyToAllProperties === null) {
        this.policy.applyToAllProperties = false
      }

      // NEW: Ensure propertyGroupIds array exists for backward compatibility
      if (!this.policy.propertyGroupIds) {
        this.policy.propertyGroupIds = []
      }

      if(this.policy.id == null) {
        this.policy.policyType = null
        this.policy.startDate = null
        this.policy.endDate = null
      }
      if(this.propertyId != null) {
        this.selectedProperties = this.selectableProperties.filter(p => p.value === this.propertyId)
        this.policy.propertyIds = [this.propertyId]
      }

      // Set selected line of coverage from API response
      if(this.policy.selectedLineOfCoverage) {
        this.selectedLineOfCoverageId = this.policy.selectedLineOfCoverage.id
      } else {
        this.selectedLineOfCoverageId = null
      }

      // Set selected coverage types from API response
      if(this.policy.selectedCoverageTypes && this.policy.selectedCoverageTypes.length > 0) {
        this.selectedCoverageTypes = this.policy.selectedCoverageTypes.map(ct => ({
          value: ct.id,
          text: ct.name
        }))
      } else {
        this.selectedCoverageTypes = []
      }

      // Load special deductible types from API response (kept for backward compat mapping below)
      const selectableSpecialDeductibleTypes = this.policy.selectableSpecialDeductibleTypes || []

      // Load selected special deductibles from API response (kept for backward compat)
      if(this.policy.selectedSpecialDeductibles && this.policy.selectedSpecialDeductibles.length > 0) {
        this.policy.specialDeductibles = this.policy.selectedSpecialDeductibles.map(sd => ({
          type: { id: sd.id, name: sd.name },
          amount: sd.amount,
          isPercentage: sd.isPercentage
        }))
      } else if (!this.policy.specialDeductibles) {
        this.policy.specialDeductibles = []
      }

      // Backward compatibility: handle old 'deductible' field if 'standardDeductible' is not present
      if(this.policy.standardDeductible === null || this.policy.standardDeductible === undefined) {
        this.policy.standardDeductible = this.policy.deductible || 0
      }

      // ── DeductibleTerms backward-compat mapping ──────────────────────────────
      // If the API returned deductibleTerms (new shape), use them directly.
      // Otherwise, derive them from the legacy standardDeductible and specialDeductibles fields
      // so the new UI is populated when editing an existing policy with the old data shape.
      if (!this.policy.deductibleTerms || this.policy.deductibleTerms.length === 0) {
        const terms = []
        // Map standardDeductible → AllPeril / ACV term
        if (this.policy.standardDeductible) {
          terms.push({
            peril: 'AllPeril',
            basis: 'ACV',
            amount: this.policy.standardDeductible,
            isPercentage: false,
            minimumAmount: null,
            maximumAmount: null,
            isPerBuilding: false,
            isIncluded: true,
            aggregateLimit: null,
            trailingAmount: null
          })
        }
        // Map each specialDeductible → individual term; use the type name as peril where possible
        if (this.policy.specialDeductibles && this.policy.specialDeductibles.length > 0) {
          this.policy.specialDeductibles.forEach(sd => {
            const perilMap = {
              'Wind': 'Wind', 'Hurricane': 'Wind', 'Flood': 'Flood',
              'Earthquake': 'Earthquake', 'Named': 'Named', 'All Peril': 'AllPeril'
            }
            const peril = perilMap[sd.type && sd.type.name] || 'Other'
            terms.push({
              peril,
              basis: 'ACV',
              amount: sd.amount,
              isPercentage: sd.isPercentage,
              minimumAmount: null,
              maximumAmount: null,
              isPerBuilding: false,
              isIncluded: true,
              aggregateLimit: null,
              trailingAmount: null
            })
          })
        }
        this.policy.deductibleTerms = terms
      }

      // ── CoverageLines backward-compat mapping ────────────────────────────────
      // If the API returned coverageLines (new shape), use them directly.
      // Otherwise, derive from the legacy selectedLineOfCoverage/selectedCoverageTypes fields.
      if (!this.policy.coverageLines || this.policy.coverageLines.length === 0) {
        if (this.policy.selectedLineOfCoverage) {
          const ctypes = (this.policy.selectedCoverageTypes || []).map(ct => ({
            value: ct.id,
            text: ct.name
          }))
          this.policy.coverageLines = [{
            lineOfCoverageId: this.policy.selectedLineOfCoverage.id,
            lineOfCoverageName: this.policy.selectedLineOfCoverage.name,
            coverageTypes: ctypes,
            limit: null,
            notes: ''
          }]
        } else {
          this.policy.coverageLines = []
        }
      }

      // SAVE A DEEP COPY as the original
      this.originalPolicy = JSON.parse(JSON.stringify(this.policy))
      this.isBusy = false
    },
    async removeProperty (id) {
      this.selectedProperties = this.selectedProperties.filter(p => p.value !== id)
      this.policy.propertyIds = this.policy.propertyIds.filter(p => p !== id)
      this.selectedProperties.sort((a, b) => a.text.localeCompare(b.text));

    },
    async itemAdded (item){
      this.policy.propertyIds.push(item.value)
      this.selectedProperties.sort((a, b) => a.text.localeCompare(b.text));
    },
    async itemRemoved (item) {
      if(item.claimCount > 0)
      {
        this.selectedProperties.push(item)
        this.$toasted.error('Properties with claims cannot be removed from the policy', { duration: 2500 })
      } else {
        this.policy.propertyIds = this.policy.propertyIds.filter(p => p !== item.value)
      }
      this.selectedProperties.sort((a, b) => a.text.localeCompare(b.text));
    },
    // NEW: Property Group Management Methods
    async propertyGroupAdded (item) {
      if (!this.policy.propertyGroupIds.includes(item.value)) {
        this.policy.propertyGroupIds.push(item.value)
      }
      this.selectedPropertyGroups.sort((a, b) => a.text.localeCompare(b.text));
    },
    async propertyGroupRemoved (item) {
      if (item.claimCount > 0) {
        // Re-add to selection if it has claims
        this.selectedPropertyGroups.push(item)
        this.$toasted.error('Property groups with claims cannot be removed from the policy', { duration: 2500 })
      } else {
        this.policy.propertyGroupIds = this.policy.propertyGroupIds.filter(id => id !== item.value)
      }
      this.selectedPropertyGroups.sort((a, b) => a.text.localeCompare(b.text));
    },
    async removePropertyGroup (id) {
      this.selectedPropertyGroups = this.selectedPropertyGroups.filter(g => g.value !== id)
      this.policy.propertyGroupIds = this.policy.propertyGroupIds.filter(gId => gId !== id)
      this.selectedPropertyGroups.sort((a, b) => a.text.localeCompare(b.text));
    },
    // NEW: Apply to All Properties Handler
    async onApplyToAllChanged (isChecked) {
      if (isChecked) {
        // User is enabling "Apply to All"
        const hasExistingSelections = 
          this.selectedProperties.length > 0 || 
          this.selectedPropertyGroups.length > 0
        
        if (hasExistingSelections) {
          const confirmed = confirm(
            'Enabling "Apply to All Properties" will clear your current property and group selections. Continue?'
          )
          
          if (!confirmed) {
            // User cancelled - revert checkbox
            this.$nextTick(() => {
              this.policy.applyToAllProperties = false
            })
            return
          }
        }
        
        // Clear selections
        this.selectedProperties = []
        this.selectedPropertyGroups = []
        this.policy.propertyIds = []
        this.policy.propertyGroupIds = []
        
        this.$toasted.success('Policy will now apply to all properties', { duration: 2000 })
      } else {
        // User is disabling "Apply to All"
        this.$toasted.info('Select individual properties or groups below', { duration: 2000 })
      }
    },
    async coverageTypeAdded (item) {
      // Multiselect v-model handles the addition automatically
    },
    async coverageTypeRemoved (item) {
      // Multiselect v-model handles the removal automatically
    },
    // ── DeductibleTerms methods ───────────────────────────────────────────────
    addDeductibleTerm () {
      this.policy.deductibleTerms.push({
        peril: null,
        basis: null,
        amount: null,
        isPercentage: false,
        minimumAmount: null,
        maximumAmount: null,
        isPerBuilding: false,
        isIncluded: true,
        aggregateLimit: null,
        trailingAmount: null
      })
    },
    removeDeductibleTerm (index) {
      if (confirm('Remove this deductible term?')) {
        this.policy.deductibleTerms.splice(index, 1)
      }
    },
    // ── CoverageLines methods ─────────────────────────────────────────────────
    addCoverageLine () {
      this.policy.coverageLines.push({
        lineOfCoverageId: null,
        lineOfCoverageName: '',
        coverageTypes: [],
        limit: null,
        notes: ''
      })
    },
    removeCoverageLine (index) {
      if (confirm('Remove this coverage line?')) {
        this.policy.coverageLines.splice(index, 1)
      }
    },
    // When a coverage line's LOC dropdown changes, auto-populate lineOfCoverageName
    // and clear coverageTypes since the new LOC has a different type set.
    onCoverageLineLocChanged (line, locId) {
      const selectedLine = this.policy.selectableLinesOfCoverage
        ? this.policy.selectableLinesOfCoverage.find(l => l.id === locId)
        : null
      line.lineOfCoverageName = selectedLine ? selectedLine.name : ''
      line.coverageTypes = []
    },
    formatCurrency(value) {
      if (value === null || value === undefined) {
        return 'N/A'
      }
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(value)
    },
    formatPercentChange(value) {
      if (value === null || value === undefined) {
        return 'N/A'
      }
      const percent = (value * 100).toFixed(2)
      const sign = value > 0 ? '+' : ''
      return `${sign}${percent}%`
    }
  },
  computed: {
    selectableProperties () {
      if(this.policy != undefined && this.policy.selectableProperties != undefined) {
        let props = this.policy.selectableProperties.map ((p) => {
          let claimText = p.claimCount > 0 ? (p.claimCount === 1 ? `${p.name}  (1 claim)` : `${p.claimCount} (claims)`): p.name
          return { value: p.id, text: claimText, claimCount: p.claimCount }
        })
        props.splice(0, 0, {text: 'Select one or more properties', value: null, disabled: true, $isDisabled: true}) 
        return props
      } else {
        return []
      }
    },
    lineOfCoverageOptions () {
      if(this.policy != undefined && this.policy.selectableLinesOfCoverage != undefined) {
        return this.policy.selectableLinesOfCoverage.map((line) => ({
          value: line.id,
          text: line.name
        }))
      } else {
        return []
      }
    },
    availableCoverageTypes () {
      if (this.selectedLineOfCoverageId && this.policy != undefined && this.policy.selectableLinesOfCoverage != undefined) {
        const selectedLine = this.policy.selectableLinesOfCoverage.find(line => line.id === this.selectedLineOfCoverageId)
        if (selectedLine && selectedLine.coverageTypes) {
          return selectedLine.coverageTypes.map(ct => ({
            value: ct.id,
            text: ct.name
          }))
        }
      }
      return []
    },
    // Returns coverage type options for a given lineOfCoverageId — used in the CoverageLines editor
    getCoverageTypesForLine () {
      return (locId) => {
        if (!locId || !this.policy || !this.policy.selectableLinesOfCoverage) return []
        const line = this.policy.selectableLinesOfCoverage.find(l => l.id === locId)
        if (line && line.coverageTypes) {
          return line.coverageTypes.map(ct => ({ value: ct.id, text: ct.name }))
        }
        return []
      }
    },
    aiSuggestionConfidenceVariant () {
      if (!this.aiSuggestion) return 'secondary'
      const pct = Math.round((this.aiSuggestion.confidenceScore || 0) * 100)
      if (pct >= 75) return 'success'
      if (pct >= 40) return 'warning'
      return 'danger'
    },
    aiSuggestionConfidenceLabel () {
      if (!this.aiSuggestion) return ''
      const pct = Math.round((this.aiSuggestion.confidenceScore || 0) * 100)
      if (pct >= 75) return `High — ${pct}%`
      if (pct >= 40) return `Medium — ${pct}%`
      return `Low — ${pct}%`
    },
    isDirty () {
      if (!this.originalPolicy) return false
      //return JSON.stringify(this.policy) !== JSON.stringify(this.originalPolicy)
      return true
    },
    validationNumberError () {
      let error = this.validationErrors.find((ve) => ve.field === 'Number')
      return error == null ? null : error.message
    },
    validationStartDateError () {
      let error = this.validationErrors.find((ve) => ve.field === 'StartDate')
      return error == null ? null : error.message
    },
    validationEndDateError () {
      let error = this.validationErrors.find((ve) => ve.field === 'EndDate')
      return error == null ? null : error.message
    },
    // NEW: Property Groups computed property
    selectablePropertyGroups () {
      if (this.policy != undefined && this.policy.selectablePropertyGroups != undefined) {
        let groups = this.policy.selectablePropertyGroups.map((g) => {
          return { 
            value: g.id, 
            text: g.name, 
            propertyCount: g.propertyCount || 0,
            claimCount: g.claimCount || 0 
          }
        })
        groups.splice(0, 0, {
          text: 'Select one or more property groups', 
          value: null, 
          disabled: true, 
          $isDisabled: true
        })
        return groups
      } else {
        return []
      }
    },
    // NEW: Determine which assignment method is currently active
    activeAssignmentMethod () {
      if (this.policy.applyToAllProperties) {
        return 'all'
      } else if (this.policy.propertyGroupIds && this.policy.propertyGroupIds.length > 0) {
        return 'groups'
      } else if (this.policy.propertyIds && this.policy.propertyIds.length > 0) {
        return 'properties'
      } else {
        return 'none'
      }
    },
    // NEW: Human-readable assignment summary
    assignmentSummary () {
      switch (this.activeAssignmentMethod) {
        case 'all':
          return 'This policy applies to ALL properties in your company'
        case 'groups':
          const groupCount = this.selectedPropertyGroups.length
          const totalProperties = this.selectedPropertyGroups.reduce((sum, g) => sum + (g.propertyCount || 0), 0)
          return `This policy applies to ${groupCount} property group${groupCount !== 1 ? 's' : ''} (${totalProperties} total properties)`
        case 'properties':
          const propCount = this.selectedProperties.length
          return `This policy applies to ${propCount} individual propert${propCount !== 1 ? 'ies' : 'y'}`
        case 'none':
          return 'No properties assigned - policy will not be active'
        default:
          return ''
      }
    },
    // NEW: CSS class for assignment summary alert
    assignmentSummaryClass () {
      switch (this.activeAssignmentMethod) {
        case 'all':
          return 'alert-success'
        case 'groups':
        case 'properties':
          return 'alert-info'
        case 'none':
          return 'alert-warning'
        default:
          return 'alert-secondary'
      }
    },
    // NEW: Icon for assignment summary
    assignmentSummaryIcon () {
      switch (this.activeAssignmentMethod) {
        case 'all':
          return 'fas fa-globe'
        case 'groups':
          return 'fas fa-layer-group'
        case 'properties':
          return 'fas fa-building'
        case 'none':
          return 'fas fa-exclamation-triangle'
        default:
          return 'fas fa-info-circle'
      }
    },
    // NEW: Check if there are validation warnings to display
    hasValidationWarnings () {
      return this.validationWarnings.length > 0
    },
    // NEW: Filter validation errors to show only property assignment warnings
    validationWarnings () {
      return this.validationErrors.filter(e => 
        e.field === 'ApplyToAllProperties' || 
        e.field === 'PropertyGroupIds' ||
        e.field === 'PropertyIds'
      )
    }
  },
  watch: {
    'policy.startDate'(newVal) {
      clearTimeout(this.startDateDebounceTimer)
      this.startDateDebounceTimer = setTimeout(() => {
        if (newVal && !this.policy.endDate) {
          if (moment(newVal).isValid()) {
            let endDate = moment(newVal).add(1, 'year').format('YYYY-MM-DD');
            this.policy.endDate = endDate
          }
        }
      }, 600)
    },
    selectedLineOfCoverageId(newVal, oldVal) {
      // Clear coverage types when line changes (but not on initial load)
      if (oldVal !== null && oldVal !== undefined && newVal !== oldVal) {
        this.selectedCoverageTypes = []
      }
    }
  }
}
</script>

<style type="text/css" scoped>
.policies-container {
  height: calc(100vh - 130px);
  overflow: auto;
}

.multiselect__tags
{
  min-height:20px;
  padding: 2px 40px 0 2px;
}

.multiselect__tags, .multiselect__input, .multiselect__single
{
    font-family: inherit;
    font-size: 12px !important;
}

/* NEW: Styling for Apply to All Properties section */
.bg-success-light {
  background-color: #d4edda !important;
}

/* NEW: Styling for disabled multiselect state */
.disabled-multiselect {
  opacity: 0.5;
  pointer-events: none;
  cursor: not-allowed;
}

.disabled-multiselect .multiselect {
  background-color: #e9ecef;
}
.brand-dark-blue {
  background-color: #13385a !important;
}
.border-left-lg {
  border-left-width: 3px;
  border-left-style: solid;
}
.tab-pane {
  padding: 0 !important;
}
</style>

<style>
.policy-tabs.tab-content {
  margin-top: 0 !important;
}
.policy-tabs .form-container.radius-bottom {
  border-radius: 0 0 10px 10px !important;
}
</style>



// WEBPACK FOOTER //
// src/views/policies/AddEditInsurancePolicy.vue